import React, { useState, useRef, useCallback, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Skeleton, Typography, IconButton, Tooltip } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WorkIcon from '@mui/icons-material/WorkOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import InsightsIcon from '@mui/icons-material/Insights';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { COLORS } from './pipeline-metrics/theme';

const PipelineMetrics = lazy(() => import('./pipeline-metrics'));
const Pipelines = lazy(() => import('./pipelines'));
const Jobs = lazy(() => import('./jobs'));
const Users = lazy(() => import('./users'));

const TAB_ITEMS = [
  { label: 'Pipeline Metrics', icon: <InsightsIcon sx={{ fontSize: 18 }} /> },
  { label: 'Pipelines', icon: <AccountTreeIcon sx={{ fontSize: 18 }} /> },
  { label: 'Jobs', icon: <WorkIcon sx={{ fontSize: 18 }} /> },
  { label: 'Users', icon: <PeopleAltIcon sx={{ fontSize: 18 }} /> },
];

const TAB_SLUGS = ['pipeline-metrics', 'pipelines', 'jobs', 'users'] as const;

const DashboardWrapper: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Math.max(0, TAB_SLUGS.indexOf(searchParams.get('tab') as typeof TAB_SLUGS[number]));
  const [tab, setTab] = useState(initialTab);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTabChange = useCallback((index: number) => {
    setTab(index);
    setSearchParams({ tab: TAB_SLUGS[index] }, { replace: true });
  }, [setSearchParams]);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  return (
    <Box ref={containerRef} sx={{ minHeight: '100vh', bgcolor: COLORS.pageBg, ...(isFullscreen && { height: '100vh', overflow: 'auto' }) }}>
      {/* ── Tab Navigation ── */}
      <Box sx={{
        bgcolor: COLORS.cardBg,
        borderBottom: `1px solid ${COLORS.border}`,
        px: { xs: 2, md: 3 },
        pt: 1.5,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
      }}>
        {TAB_ITEMS.map((item, i) => {
          const isActive = tab === i;
          return (
            <Box
              key={item.label}
              onClick={() => handleTabChange(i)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2,
                py: 1.2,
                cursor: 'pointer',
                borderRadius: '8px 8px 0 0',
                position: 'relative',
                transition: 'all 0.2s ease',
                bgcolor: isActive ? COLORS.borderLight : 'transparent',
                '&:hover': {
                  bgcolor: isActive ? COLORS.borderLight : COLORS.pageBg,
                },
                '&::after': isActive ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2.5px',
                  bgcolor: COLORS.darkText,
                  borderRadius: '2px 2px 0 0',
                } : {},
              }}
            >
              <Box sx={{ color: isActive ? COLORS.darkText : '#94a3b8', display: 'flex', transition: 'color 0.2s' }}>
                {item.icon}
              </Box>
              <Typography sx={{
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? COLORS.darkText : COLORS.secondaryText,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
          <IconButton onClick={toggleFullscreen} size="small"
            sx={{ mb: 0.5, color: COLORS.secondaryText, '&:hover': { color: COLORS.darkText, bgcolor: COLORS.borderLight } }}>
            {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 20 }} /> : <FullscreenIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Suspense
        fallback={
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton width={200} height={28} />
            <Skeleton variant="rounded" width="100%" height={60} sx={{ borderRadius: '12px' }} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} variant="rounded" width="100%" height={80} sx={{ flex: 1, borderRadius: '12px' }} />
              ))}
            </Box>
          </Box>
        }
      >
        {tab === 0 && <PipelineMetrics />}
        {tab === 1 && <Pipelines />}
        {tab === 2 && <Jobs />}
        {tab === 3 && <Users />}
      </Suspense>
    </Box>
  );
};

export default DashboardWrapper;
